// REACT
import React, { useEffect, useState } from "react";

// REACT BOOTSTRAP
import { Col, Row } from "react-bootstrap";

// REDUX
import { useDispatch, useSelector } from "react-redux";

// REACT ROUTER
import { Link, useParams } from "react-router-dom";

// COMPONENTS
import Form from "components/Form";
import ImageUpload from "components/Form/image-upload";
import Textarea from "components/Form/textarea/index";
import Input from "components/Form/input";
import SaveChangesButton from "components/Form/save-changes-button";
import FormWrapper from "components/Form/form-wrapper";

// STORE ACTIONS
import { getAllEventGroups, editApplication } from "store/actions";

// ASSETS
import { ReactComponent as EditIcon } from "./assets/edit.svg";

// HELPER FUNCTIONS
import { cutImageFullPath } from "helpers/functions";

// INTERNATIONALIZATION
import { FormattedMessage, useIntl } from "react-intl";
import { validateUrl } from "helpers/functions";

const EditApplicationWrapper = () => {
    const { locale, formatMessage } = useIntl();
    const dispatch = useDispatch();
    const [editAppInfoCheck, setEditAppInfoCheck] = useState(false);
    const { singleApplication } = useSelector((state) => state.applications);
    const { applicationId } = useParams();

    useEffect(() => {
        dispatch(getAllEventGroups());
    }, []);

    const handleWebsiteLink = (website) => {
        if (
            website &&
            !website.startsWith("http://") &&
            !website.startsWith("https://")
        ) {
            return `https://${website}`;
        }
        return website;
    };

    let defaultValues = {
        name: singleApplication?.name || {},
        icon: singleApplication?.icon || "",
        website: singleApplication?.website || "",
        supportEmail: singleApplication?.supportEmail || "",
        description: singleApplication?.description || {},
    };

    const onSubmitDetails = (data) => {
        data.icon = cutImageFullPath(data?.icon);
        dispatch(
            editApplication({ endpointName: "info", id: applicationId, data })
        );
        setEditAppInfoCheck(!editAppInfoCheck);
    };

    return (
        <div className="edit-application">
            <FormWrapper>
                <Form onSubmit={onSubmitDetails} defaultValues={defaultValues}>
                    <span
                        onClick={() => setEditAppInfoCheck(!editAppInfoCheck)}
                    >
                        <EditIcon className="edit-icon" />
                    </span>
                    <Row>
                        <Col md={3} xs={12}>
                            <div className="image-container">
                                <ImageUpload
                                    name="icon"
                                    langId={false}
                                    classNames="mb-auto"
                                    rules={{}}
                                />
                            </div>
                        </Col>
                        {!editAppInfoCheck ? (
                            <Col>
                                <div className="info-container">
                                    <Row>
                                        <Col xs="12">
                                            <span>
                                                {singleApplication?.name?.[
                                                    locale
                                                ] || "name"}
                                            </span>
                                        </Col>
                                        <Col xs="12">
                                            <div className="data">
                                                <div className="data-item">
                                                    <span className="key">
                                                        <FormattedMessage id="appNumber" />{" "}
                                                        :{" "}
                                                    </span>
                                                    <span className="value">
                                                        {singleApplication?.number ||
                                                            "2132"}
                                                    </span>
                                                </div>

                                                <div className="data-item">
                                                    <a
                                                        target="_blank"
                                                        href={handleWebsiteLink(
                                                            singleApplication?.website
                                                        )}
                                                        rel="noreferrer"
                                                    >
                                                        <FormattedMessage id="appLink" />
                                                    </a>
                                                </div>
                                                <div className="data-item">
                                                    <a
                                                        href={`mailto:${singleApplication?.supportEmail}`}
                                                    >
                                                        <FormattedMessage id="supportMail" />
                                                    </a>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <hr />
                                    {singleApplication?.description?.[
                                        locale
                                    ] && (
                                        <>
                                            <Row>
                                                <Col xs="12">
                                                    <h5 className="description">
                                                        {
                                                            singleApplication
                                                                ?.description?.[
                                                                locale
                                                            ]
                                                        }
                                                    </h5>
                                                </Col>
                                            </Row>
                                            <hr />
                                        </>
                                    )}

                                    <div className="data">
                                        <div className="data-item">
                                            <span className="key">
                                                <FormattedMessage id="subscriptions" />{" "}
                                                :{" "}
                                            </span>
                                            <span className="value">
                                                {singleApplication?.activeAppSubscriptionCount ||
                                                    "0"}
                                            </span>
                                        </div>
                                        <div className="data-item">
                                            <span className="key">
                                                <FormattedMessage id="testSubscriptions" />{" "}
                                                :{" "}
                                            </span>
                                            <span className="value">
                                                {singleApplication?.testSubscriptions ||
                                                    "0"}
                                            </span>
                                        </div>
                                        <div className="data-item">
                                            <span className="key">
                                                <FormattedMessage id="endedSubscriptions" />{" "}
                                                :{" "}
                                            </span>
                                            <span className="value">
                                                {singleApplication?.inactiveAppSubscriptionCount ||
                                                    "0"}
                                            </span>
                                        </div>
                                        <div className="data-item">
                                            <span className="key">
                                                <FormattedMessage id="endedTestSubscriptions" />{" "}
                                                :{" "}
                                            </span>
                                            <span className="value">
                                                {singleApplication?.endedTestSubscriptions ||
                                                    "0"}
                                            </span>
                                        </div>
                                        <div className="data-item">
                                            <Link
                                                to="revisions/"
                                                className="cursor-pointer"
                                            >
                                                <FormattedMessage id="revisions" />{" "}
                                                :{" "}
                                                <span className="value">
                                                    {singleApplication?.reviewsCount ||
                                                        "0"}
                                                </span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ) : (
                            <>
                                <Row>
                                    <Input
                                        name="name"
                                        locale="ar"
                                        langId="name"
                                        rules={{
                                            required: formatMessage({
                                                id: "requiredField",
                                            }),
                                        }}
                                    />
                                    <Input
                                        name="name"
                                        locale="en"
                                        langId="name"
                                        rules={{
                                            required: formatMessage({
                                                id: "requiredField",
                                            }),
                                        }}
                                    />

                                    <Textarea
                                        name="description"
                                        locale="ar"
                                        langId="description"
                                        rules={{
                                            required: formatMessage({
                                                id: "requiredField",
                                            }),
                                        }}
                                    />
                                    <Textarea
                                        name="description"
                                        locale="en"
                                        langId="description"
                                        rules={{
                                            required: formatMessage({
                                                id: "requiredField",
                                            }),
                                        }}
                                    />

                                    <Input
                                        name="website"
                                        langId="appLink"
                                        rules={{
                                            required: formatMessage({
                                                id: "requiredField",
                                            }),
                                            validate: (value) =>
                                                validateUrl(value) ||
                                                formatMessage({
                                                    id: "invalidUrl",
                                                }),
                                        }}
                                    />
                                    <Input
                                        name="supportEmail"
                                        langId="supportEmail"
                                        rules={{
                                            required: formatMessage({
                                                id: "requiredField",
                                            }),
                                            pattern: {
                                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                                message: formatMessage({
                                                    id: "invalidEmail",
                                                }),
                                            },
                                        }}
                                    />
                                </Row>

                                <SaveChangesButton />
                            </>
                        )}
                    </Row>
                </Form>
            </FormWrapper>
        </div>
    );
};

export default EditApplicationWrapper;
